import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  Send, 
  ArrowLeft,
  Calendar,
  CheckCircle,
  AlertCircle,
  Trash2,
  X
} from 'lucide-react';
import { invoicesAPI, profileAPI, receiptsAPI } from '../services/api';
import { InvoiceDoc, CompanyProfileDoc } from '../components/documents/types';
import InvoicePrintable from '../components/documents/InvoicePrintable';
import { downloadPdf } from '../utils/downloadPdf';

const InvoiceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<InvoiceDoc | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfileDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');
  const [useCustomReceiptNumber, setUseCustomReceiptNumber] = useState<boolean>(false);
  const [customReceiptNumber, setCustomReceiptNumber] = useState<string>('');
  const [paymentMode, setPaymentMode] = useState<string>('Bank Transfer');
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [paymentDetailsNotNeeded, setPaymentDetailsNotNeeded] = useState<boolean>(false);

  // Check if invoice number is custom (doesn't match auto-generated pattern)
  const isCustomInvoiceNumber = (invoiceNumber: string, invoicePrefix: string): boolean => {
    if (!invoicePrefix || !invoiceNumber) return false;
    // Auto-generated pattern: PREFIX + 6 digits (e.g., INV000001)
    const autoGeneratedPattern = new RegExp(`^${invoicePrefix}\\d{6}$`);
    return !autoGeneratedPattern.test(invoiceNumber);
  };

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    
    try {
      const [invoiceRes, profileRes] = await Promise.all([
        invoicesAPI.getById(id),
        profileAPI.get().catch(() => null) // Don't fail if profile fetch fails
      ]);
      
      setInvoice(invoiceRes.data);
      if (profileRes?.data) {
        setCompanyProfile(profileRes.data);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setMessage({ type: 'error', text: 'Failed to load invoice' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleSendEmail = async () => {
    if (!invoice) return;
    
    setSendingEmail(true);
    try {
      await invoicesAPI.sendEmail(invoice._id);
      setMessage({ type: 'success', text: 'Invoice sent successfully!' });
      // Refresh invoice to get updated status
      fetchInvoice();
    } catch (error) {
      console.error('Error sending email:', error);
      setMessage({ type: 'error', text: 'Failed to send invoice email' });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (!invoice) return;
    
    setGeneratingPDF(true);
    try {
      await downloadPdf('invoice-content', `Invoice-${invoice.invoiceNumber}.pdf`);
      setMessage({ type: 'success', text: 'PDF generated successfully!' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setMessage({ type: 'error', text: 'Failed to generate PDF' });
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleStatusChangeClick = (status: string) => {
    if (status === 'Paid' && invoice?.status !== 'Paid') {
      // Always show modal when marking as Paid to collect payment details
      setNewStatus(status);
      setShowStatusChangeModal(true);
      setUseCustomReceiptNumber(false);
      setCustomReceiptNumber('');
      setPaymentMode('Bank Transfer');
      setReferenceNumber('');
      setPaymentDetailsNotNeeded(false);
    } else {
      handleStatusChange(status);
    }
  };

  const handleStatusChange = async (status?: string) => {
    if (!invoice) return;
    
    const statusToUpdate = status || newStatus;
    setUpdatingStatus(true);
    setMessage(null);
    
    try {
      await invoicesAPI.updateStatus(invoice._id, statusToUpdate);
      setMessage({ type: 'success', text: `Invoice status updated to ${statusToUpdate}!` });
      
      // Refresh invoice data
      fetchInvoice();
      
      // Auto-generate receipt if status changed to Paid
      if (statusToUpdate === 'Paid') {
        try {
          const receiptData: any = {
            paymentMode: paymentDetailsNotNeeded ? 'Bank Transfer' : paymentMode
          };
          
          if (useCustomReceiptNumber && customReceiptNumber.trim()) {
            receiptData.customReceiptNumber = customReceiptNumber.trim();
          }
          
          // Add payment details if not skipped
          if (!paymentDetailsNotNeeded && referenceNumber.trim()) {
            receiptData.paymentDetails = {
              referenceNumber: referenceNumber.trim()
            };
          }
          
          await receiptsAPI.generateFromInvoice(invoice._id, receiptData);
          setMessage({ type: 'success', text: 'Invoice marked as paid and receipt generated successfully!' });
        } catch (receiptError) {
          console.error('Error generating receipt:', receiptError);
          // Don't show error if receipt already exists
          const error = receiptError as any;
          if (error?.response?.status !== 400) {
            setMessage({ type: 'error', text: 'Status updated but failed to generate receipt' });
          }
        }
      }
      
      // Close modal and reset form
      setShowStatusChangeModal(false);
      setUseCustomReceiptNumber(false);
      setCustomReceiptNumber('');
      setPaymentMode('Bank Transfer');
      setReferenceNumber('');
      setPaymentDetailsNotNeeded(false);
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage({ type: 'error', text: 'Failed to update invoice status' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleGenerateReceipt = async () => {
    if (!invoice) return;
    
    // Check if invoice has custom invoice number
    const invoicePrefix = companyProfile?.invoiceNumberPrefix || 'INV';
    const hasCustomInvoiceNumber = isCustomInvoiceNumber(invoice.invoiceNumber, invoicePrefix);
    
    if (hasCustomInvoiceNumber) {
      // Show modal for custom invoice numbers
      setNewStatus('Paid');
      setShowStatusChangeModal(true);
      setUseCustomReceiptNumber(false);
      setCustomReceiptNumber('');
    } else {
      // Auto-generated invoice - directly generate receipt
      setGeneratingReceipt(true);
      setMessage(null);
      
      try {
        await receiptsAPI.generateFromInvoice(invoice._id);
        setMessage({ type: 'success', text: 'Receipt generated successfully!' });
        fetchInvoice();
      } catch (error) {
        console.error('Error generating receipt:', error);
        setMessage({ type: 'error', text: 'Failed to generate receipt' });
      } finally {
        setGeneratingReceipt(false);
      }
    }
  };

  const handleGenerateReceiptConfirm = async () => {
    if (!invoice) return;
    
    setGeneratingReceipt(true);
    setMessage(null);
    
    try {
      const receiptData: any = {
        paymentMode: paymentDetailsNotNeeded ? 'Bank Transfer' : paymentMode
      };
      
      if (useCustomReceiptNumber && customReceiptNumber.trim()) {
        receiptData.customReceiptNumber = customReceiptNumber.trim();
      }
      
      // Add payment details if not skipped
      if (!paymentDetailsNotNeeded && referenceNumber.trim()) {
        receiptData.paymentDetails = {
          referenceNumber: referenceNumber.trim()
        };
      }
      
      await receiptsAPI.generateFromInvoice(invoice._id, receiptData);
      setMessage({ type: 'success', text: 'Receipt generated successfully!' });
      setShowStatusChangeModal(false);
      setUseCustomReceiptNumber(false);
      setCustomReceiptNumber('');
      setPaymentMode('Bank Transfer');
      setReferenceNumber('');
      setPaymentDetailsNotNeeded(false);
      fetchInvoice();
    } catch (error) {
      console.error('Error generating receipt:', error);
      setMessage({ type: 'error', text: 'Failed to generate receipt' });
    } finally {
      setGeneratingReceipt(false);
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    
    setDeleting(true);
    setMessage(null);
    
    try {
      await invoicesAPI.delete(invoice._id);
      setMessage({ type: 'success', text: 'Invoice deleted successfully!' });
      setTimeout(() => {
        navigate('/invoices');
      }, 1500);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setMessage({ type: 'error', text: 'Failed to delete invoice' });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-3">
            <FileText className="h-8 w-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
              <p className="text-gray-600">Invoice Details</p>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={invoice.status}
            onChange={(e) => handleStatusChangeClick(e.target.value)}
            disabled={updatingStatus}
            className="form-input pr-8"
          >
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
          </select>
        </div>
      </div>

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4"
      >
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Created: {formatDate(invoice.createdAt)}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Due: {formatDate(invoice.dueDate)}</span>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {invoice.status === 'Paid' && (
            <button
              onClick={handleGenerateReceipt}
              disabled={generatingReceipt}
              className="btn-secondary flex items-center space-x-2"
            >
              <FileText className="h-4 w-4" />
              <span>{generatingReceipt ? 'Generating...' : 'Generate Receipt'}</span>
            </button>
          )}
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="btn-secondary flex items-center space-x-2"
          >
            <Send className="h-4 w-4" />
            <span>{sendingEmail ? 'Sending...' : 'Send Email'}</span>
          </button>
          <button
            onClick={handleGeneratePDF}
            disabled={generatingPDF}
            className="btn-primary flex items-center space-x-2"
          >
            <Download className="h-4 w-4" />
            <span>{generatingPDF ? 'Generating...' : 'Download PDF'}</span>
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-secondary text-red-600 hover:bg-red-50 flex items-center space-x-2 border-red-200"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </motion.div>

      {/* Invoice Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <InvoicePrintable invoice={invoice} company={companyProfile} />
      </motion.div>

      {/* Status Change Modal with Custom Receipt Number */}
      {showStatusChangeModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {invoice.status === 'Paid' ? 'Generate Receipt' : 'Change Status to Paid'}
              </h3>
              <button
                onClick={() => {
                  setShowStatusChangeModal(false);
                  setUseCustomReceiptNumber(false);
                  setCustomReceiptNumber('');
                  setPaymentMode('Bank Transfer');
                  setReferenceNumber('');
                  setPaymentDetailsNotNeeded(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                {invoice.status === 'Paid' 
                  ? 'This will generate a receipt for this invoice.'
                  : 'This will mark the invoice as paid and generate a receipt.'}
              </p>
              
              {/* Payment Details Section */}
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <label className="flex items-center space-x-2 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={paymentDetailsNotNeeded}
                    onChange={(e) => {
                      setPaymentDetailsNotNeeded(e.target.checked);
                      if (e.target.checked) {
                        setReferenceNumber('');
                      }
                    }}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Payment details not needed?</span>
                </label>
                
                {!paymentDetailsNotNeeded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="form-label">Mode of Transfer *</label>
                      <select
                        value={paymentMode}
                        onChange={(e) => setPaymentMode(e.target.value)}
                        className="form-input"
                        required
                      >
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="UPI">UPI</option>
                        <option value="NEFT">NEFT</option>
                        <option value="RTGS">RTGS</option>
                        <option value="IMPS">IMPS</option>
                        <option value="Cash">Cash</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Card">Card</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="form-label">Reference Number *</label>
                      <input
                        type="text"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        className="form-input"
                        placeholder="Enter reference/transaction number"
                        required
                      />
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Custom Receipt Number Section - Only show if invoice has custom invoice number */}
              {(() => {
                const invoicePrefix = companyProfile?.invoiceNumberPrefix || 'INV';
                const hasCustomInvoiceNumber = invoice && isCustomInvoiceNumber(invoice.invoiceNumber, invoicePrefix);
                
                return hasCustomInvoiceNumber ? (
                  <div className="mt-4">
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-1">Custom Invoice Detected</p>
                      <p className="text-sm text-blue-700">
                        This invoice has a custom invoice number: <strong>{invoice.invoiceNumber}</strong>
                      </p>
                    </div>
                    
                    <label className="flex items-center space-x-2 cursor-pointer mb-4">
                      <input
                        type="checkbox"
                        checked={useCustomReceiptNumber}
                        onChange={(e) => {
                          setUseCustomReceiptNumber(e.target.checked);
                          if (!e.target.checked) {
                            setCustomReceiptNumber('');
                          }
                        }}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Do you need to pass custom receipt number?</span>
                    </label>
                    
                    {useCustomReceiptNumber && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4"
                      >
                        <label className="form-label">Custom Receipt Number *</label>
                        <input
                          type="text"
                          value={customReceiptNumber}
                          onChange={(e) => setCustomReceiptNumber(e.target.value)}
                          className="form-input"
                          placeholder="Enter custom receipt number"
                          required={useCustomReceiptNumber}
                        />
                      </motion.div>
                    )}
                  </div>
                ) : null;
              })()}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowStatusChangeModal(false);
                  setUseCustomReceiptNumber(false);
                  setCustomReceiptNumber('');
                  setPaymentMode('Bank Transfer');
                  setReferenceNumber('');
                  setPaymentDetailsNotNeeded(false);
                }}
                disabled={updatingStatus || generatingReceipt}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (invoice.status === 'Paid') {
                    handleGenerateReceiptConfirm();
                  } else {
                    handleStatusChange();
                  }
                }}
                disabled={updatingStatus || generatingReceipt || (useCustomReceiptNumber && !customReceiptNumber.trim()) || (!paymentDetailsNotNeeded && (!paymentMode || !referenceNumber.trim()))}
                className="btn-primary"
              >
                {updatingStatus || generatingReceipt ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Delete Invoice</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <AlertCircle className="h-12 w-12 text-red-600" />
                <div>
                  <p className="text-gray-900 font-medium">Are you sure you want to delete this invoice?</p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone. This will permanently delete invoice {invoice.invoiceNumber}.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn-primary bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Deleting...' : 'Delete Invoice'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default InvoiceDetails;
